import { useState } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useV2 } from '@/contexts/V2Context';
import { OperationsView } from '@/components/operations/OperationsView';
import { ENTITIES, EntityKey } from '@/components/entities/registry';
import { WorkOrderDetailDialog } from '@/components/work-orders/WorkOrderDetailDialog';
import { WorkOrderConfig } from '@/components/work-orders/types';
import { SalesOrderDetailDialog } from '@/components/sales-orders/SalesOrderDetailDialog';
import type { SalesOrderRow, SalesOrderWorkOrder } from '@/components/sales-orders/types';
import { V2Dashboard } from './V2Dashboard';
import { V2Toggle } from './V2Toggle';
import { SavedViewsPage, OpenTarget } from './SavedViewsPage';
import { LocationStateToggle } from './LocationStateToggle';
import { cn } from '@/lib/utils';

interface ClickableRow {
  workOrderId: string;
  orderNumber: string;
  customer: string;
  location?: string;
  date?: string;
  isTKS0026?: boolean;
  config: WorkOrderConfig;
}

type Tab = 'dashboard' | EntityKey | 'saved-views';

const TABS: { id: Tab; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'operations', label: 'Operations' },
  { id: 'work-orders', label: 'Work Orders' },
  { id: 'sales-orders', label: 'Sales Orders' },
  { id: 'saved-views', label: 'Saved Views' },
];

const ACTIVE_TAB_KEY = 'pp_v2_active_tab';

export function V2Shell() {
  const { logout } = useAuth();
  const { enabled } = useV2();

  const [activeTab, setActiveTabState] = useState<Tab>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(ACTIVE_TAB_KEY) : null;
    const match = TABS.find((t) => t.id === saved);
    return match ? match.id : 'dashboard';
  });

  const [openContext, setOpenContext] = useState<{
    tab: Tab;
    viewId?: string;
    token: number;
  } | null>(null);

  const setActiveTab = (tab: Tab) => {
    setActiveTabState(tab);
    localStorage.setItem(ACTIVE_TAB_KEY, tab);
  };

  const handleOpenSaved = (target: OpenTarget) => {
    const tab = target.tab as Tab;
    setOpenContext({
      tab,
      viewId: target.viewId,
      token: (openContext?.token ?? 0) + 1,
    });
    setActiveTab(tab);
  };

  if (!enabled) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div
              className="text-2xl text-slate-900 leading-none"
              style={{ fontFamily: '"Caveat", cursive', fontWeight: 700 }}
            >
              Templar
            </div>
            <nav className="flex items-center gap-1">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                    activeTab === tab.id
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <LocationStateToggle />
            <V2Toggle />
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main>
        {activeTab === 'dashboard' && (
          <V2Dashboard
            key={`dashboard-${openContext?.tab === 'dashboard' ? openContext.token : 'default'}`}
          />
        )}
        {activeTab === 'saved-views' && <SavedViewsPage onOpen={handleOpenSaved} />}
        {(activeTab === 'operations' ||
          activeTab === 'work-orders' ||
          activeTab === 'sales-orders') && (
          <EntityTab
            key={`${activeTab}-${openContext?.tab === activeTab ? openContext.token : 'default'}`}
            entityKey={activeTab}
            initialViewId={openContext?.tab === activeTab ? openContext.viewId : undefined}
          />
        )}
      </main>
    </div>
  );
}

function EntityTab({ entityKey, initialViewId }: { entityKey: EntityKey; initialViewId?: string }) {
  const config = ENTITIES[entityKey];
  const [activeWO, setActiveWO] = useState<ClickableRow | null>(null);
  const [activeSO, setActiveSO] = useState<SalesOrderRow | null>(null);

  const handleRowClick = (row: unknown) => {
    if (!row) return;
    if (entityKey === 'sales-orders') {
      setActiveSO(row as SalesOrderRow);
      return;
    }
    const r = row as ClickableRow;
    if (r.config) setActiveWO(r);
  };

  // Inside the SO dialog, drilling into a WO sets activeWO using the SO's
  // identity for the header and the WO's config for the body.
  const openWorkOrderFromSO = (wo: SalesOrderWorkOrder) => {
    if (!activeSO) return;
    setActiveWO({
      workOrderId: wo.workOrderId,
      orderNumber: activeSO.orderNumber,
      customer: activeSO.customer,
      location: activeSO.location,
      date: activeSO.date,
      isTKS0026: wo.isTKS0026,
      config: wo.config,
    });
  };

  return (
    <>
      <OperationsView
        key={config.key}
        columns={config.columns}
        useData={config.useData}
        savedViewsEntityKey={config.key}
        defaultSort={config.defaultSort}
        titleColumnId={config.titleColumnId}
        stageAccessor={config.stageAccessor}
        entityLabel={config.pluralLabel}
        showSavedViewPicker
        initialViewId={initialViewId}
        onRowClick={handleRowClick}
      />
      {activeSO && (
        <SalesOrderDetailDialog
          open={true}
          onOpenChange={(o) => !o && setActiveSO(null)}
          orderNumber={activeSO.orderNumber}
          customer={activeSO.customer}
          location={activeSO.location}
          date={activeSO.date}
          workOrders={activeSO.workOrders}
          onOpenWorkOrder={openWorkOrderFromSO}
        />
      )}
      {activeWO && (
        <WorkOrderDetailDialog
          open={true}
          onOpenChange={(o) => !o && setActiveWO(null)}
          workOrderId={activeWO.workOrderId}
          orderNumber={activeWO.orderNumber}
          customer={activeWO.customer}
          location={activeWO.location}
          date={activeWO.date}
          isTKS0026={activeWO.isTKS0026}
          config={activeWO.config}
        />
      )}
    </>
  );
}
